/**
 * Mega Menu Functionality
 * Handles expand/collapse behavior for sub-collection products
 */

document.addEventListener('DOMContentLoaded', function() {
  const megaMenuToggles = document.querySelectorAll('[data-megamenu-toggle]');
  const navToggleButtons = document.querySelectorAll('[data-megamenu-nav-toggle]');
  const collectionProductsButtons = document.querySelectorAll('[data-collection-products-button]');
  
  if (!megaMenuToggles.length && !navToggleButtons.length && !collectionProductsButtons.length) return;

  /**
   * Check if we're on mobile (below 990px)
   * @returns {boolean} True if mobile screen size
   */
  function isMobile() {
    return window.innerWidth < 990;
  }

  /**
   * Check if we're on desktop (990px and above)
   * @returns {boolean} True if desktop screen size
   */
  function isDesktop() {
    return window.innerWidth >= 990;
  }

  /**
   * Toggle column visibility for mobile navigation
   * @param {boolean} hideColumn1 - Whether to hide column 1
   */
  function toggleMobileColumns(hideColumn1) {
    if (!isMobile()) return;
    
    const column1 = document.querySelector('[data-megamenu-column-1]');
    const column2 = document.querySelector('[data-megamenu-column-2]');
    
    if (!column1 || !column2) return;
    
    if (hideColumn1) {
      column1.classList.add('megamenu__column-1--hidden');
      column2.classList.add('megamenu__column-2--full-width');
    } else {
      column1.classList.remove('megamenu__column-1--hidden');
      column2.classList.remove('megamenu__column-2--full-width');
    }
  }

  /**
   * Auto-expand first accordion item in active panel
   * @param {HTMLElement} activePanel - The active content panel
   */
  function autoExpandFirstAccordion(activePanel) {
    if (!activePanel) return;
    
    // Find first accordion item with children
    const firstAccordionItem = activePanel.querySelector('.megamenu__collection-product--has-children');
    if (!firstAccordionItem) return;
    
    // Find the toggle button within this item
    const toggleButton = firstAccordionItem.querySelector('[data-megamenu-toggle]');
    if (!toggleButton) return;
    
    // Get the target element - search within the active panel to avoid duplicate ID issues
    const targetId = toggleButton.getAttribute('aria-controls');
    if (!targetId) return;
    
    // Find target element within the activePanel context (fixes duplicate ID issue)
    const targetElement = activePanel.querySelector(`#${targetId}`);
    if (!targetElement) return;
    
    // Check if both button AND target are already properly expanded
    const buttonExpanded = toggleButton.getAttribute('aria-expanded') === 'true';
    const targetVisible = targetElement.getAttribute('aria-hidden') === 'false';
    
    if (buttonExpanded && targetVisible) return;
    
    // Expand the first accordion item (fixes inconsistent states from Liquid template)
    toggleButton.setAttribute('aria-expanded', 'true');
    targetElement.setAttribute('aria-hidden', 'false');
    
    // Force reflow to ensure attribute changes are applied
    void /** @type {HTMLElement} */ (targetElement).offsetHeight;
    
    toggleButton.classList.add('megamenu__collection-product-button--expanded');
    targetElement.classList.add('megamenu__sub-collection-products--expanded');
  }

  /**
   * Activate first mega menu panel and nav button (desktop only)
   */
  function activateFirstPanelOnDesktop() {
    if (!isDesktop()) return;
    
    const firstNavButton = navToggleButtons[0];
    if (!firstNavButton) return;
    
    const targetPanelId = firstNavButton.getAttribute('aria-controls');
    if (!targetPanelId) return;
    
    const targetPanel = document.getElementById(targetPanelId);
    if (!targetPanel) return;
    
    // Update all nav buttons - remove active state
    navToggleButtons.forEach(button => {
      button.setAttribute('aria-expanded', 'false');
      button.classList.remove('nav__link--active');
    });
    
    // Update all content panels - hide them
    const allPanels = document.querySelectorAll('.megamenu__content-panel');
    allPanels.forEach(panel => {
      panel.setAttribute('aria-hidden', 'true');
      panel.classList.remove('megamenu__content-panel--active');
    });
    
    // Activate first button and its panel
    firstNavButton.setAttribute('aria-expanded', 'true');
    firstNavButton.classList.add('nav__link--active');
    targetPanel.setAttribute('aria-hidden', 'false');
    targetPanel.classList.add('megamenu__content-panel--active');
    
    // Auto-expand first accordion item in the activated panel
    setTimeout(() => {
      autoExpandFirstAccordion(targetPanel);
    }, 100);
  }

  /**
   * Initialize mega menu toggle functionality
   */
  function initMegaMenuToggles() {
    megaMenuToggles.forEach(toggleButton => {
      toggleButton.addEventListener('click', handleToggleClick);
    });
  }

  /**
   * Initialize navigation toggle functionality (column 1 buttons)
   */
  function initNavToggles() {
    navToggleButtons.forEach(navButton => {
      navButton.addEventListener('click', handleNavToggleClick);
    });
  }

  /**
   * Initialize collection products button functionality
   */
  function initCollectionProductsButtons() {
    collectionProductsButtons.forEach(button => {
      button.addEventListener('click', handleCollectionProductsButtonClick);
    });
  }

  /**
   * Handle navigation toggle click (switching between main menu sections)
   * @param {Event} event - The click event
   */
  function handleNavToggleClick(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const clickedButton = /** @type {HTMLButtonElement} */ (event.currentTarget);
    if (!clickedButton) return;
    
    // Check if this button is already active - if so, don't do anything
    const isAlreadyActive = clickedButton.classList.contains('nav__link--active');
    if (isAlreadyActive) return;
    
    const targetPanelId = clickedButton.getAttribute('aria-controls');
    if (!targetPanelId) return;
    
    const targetPanel = document.getElementById(targetPanelId);
    if (!targetPanel) return;
    
    // Close all sub-collections when switching panels
    closeAllSubCollections();
    
    // Update all nav buttons - remove active state
    navToggleButtons.forEach(button => {
      button.setAttribute('aria-expanded', 'false');
      button.classList.remove('nav__link--active');
    });
    
    // Update all content panels - hide them
    const allPanels = document.querySelectorAll('.megamenu__content-panel');
    allPanels.forEach(panel => {
      panel.setAttribute('aria-hidden', 'true');
      panel.classList.remove('megamenu__content-panel--active');
    });
    
    // Activate clicked button and its panel
    clickedButton.setAttribute('aria-expanded', 'true');
    clickedButton.classList.add('nav__link--active');
    targetPanel.setAttribute('aria-hidden', 'false');
    targetPanel.classList.add('megamenu__content-panel--active');
    
    // Hide column 1 on mobile when nav item is selected
    toggleMobileColumns(true);
    
    // Auto-expand first accordion item in the activated panel
    setTimeout(() => {
      autoExpandFirstAccordion(targetPanel);
    }, 100);
  }

  /**
   * Handle collection products button click (closes current panel)
   * @param {Event} event - The click event
   */
  function handleCollectionProductsButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();
    
    // Close all sub-collections
    closeAllSubCollections();
    
    // Update all nav buttons - remove active state
    navToggleButtons.forEach(button => {
      button.setAttribute('aria-expanded', 'false');
      button.classList.remove('nav__link--active');
    });
    
    // Update all content panels - hide them
    const allPanels = document.querySelectorAll('.megamenu__content-panel');
    allPanels.forEach(panel => {
      panel.setAttribute('aria-hidden', 'true');
      panel.classList.remove('megamenu__content-panel--active');
    });
    
    // Show column 1 on mobile when going back
    toggleMobileColumns(false);
  }

  /**
   * Handle toggle button click events
   * @param {Event} event - The click event
   */
  function handleToggleClick(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const toggleButton = /** @type {HTMLButtonElement} */ (event.currentTarget);
    if (!toggleButton) return;
    
    const isExpanded = toggleButton.getAttribute('aria-expanded') === 'true';
    const targetId = toggleButton.getAttribute('aria-controls');
    if (!targetId) return;
    
    // Find the parent megamenu panel to search within (fixes duplicate ID issue)
    const parentPanel = toggleButton.closest('.megamenu__content-panel');
    const targetElement = parentPanel ? parentPanel.querySelector(`#${targetId}`) : document.getElementById(targetId);
    if (!targetElement) return;
    
    const newState = !isExpanded;
    
    // Close other open sub-collections in the same mega menu
    closeOtherSubCollections(toggleButton);
    
    // Update ARIA states
    toggleButton.setAttribute('aria-expanded', newState.toString());
    targetElement.setAttribute('aria-hidden', (!newState).toString());
    
    // Update visual classes
    toggleButton.classList.toggle('megamenu__collection-product-button--expanded', newState);
    targetElement.classList.toggle('megamenu__sub-collection-products--expanded', newState);
    
    // Handle focus for accessibility
    if (newState) {
      // Focus first link in expanded sub-collection
      const firstLink = /** @type {HTMLElement} */ (targetElement.querySelector('.megamenu__sub-collection-product-link'));
      if (firstLink) {
        setTimeout(() => firstLink.focus(), 100);
      }
    }
  }

  /**
   * Close other open sub-collections within the same mega menu
   * @param {HTMLElement} currentToggle - The currently clicked toggle button
   */
  function closeOtherSubCollections(currentToggle) {
    // Find the parent mega menu panel
    const parentPanel = currentToggle.closest('.megamenu__content-panel');
    if (!parentPanel) return;
    
    // Find all toggle buttons within this panel
    const siblingToggles = parentPanel.querySelectorAll('[data-megamenu-toggle]');
    
    siblingToggles.forEach(toggle => {
      if (toggle === currentToggle) return;
      
      const targetId = toggle.getAttribute('aria-controls');
      if (!targetId) return;
      
      // Search within the same panel context (fixes duplicate ID issue)
      const targetElement = parentPanel.querySelector(`#${targetId}`);
      if (targetElement) {
        toggle.setAttribute('aria-expanded', 'false');
        targetElement.setAttribute('aria-hidden', 'true');
        toggle.classList.remove('megamenu__collection-product-button--expanded');
        targetElement.classList.remove('megamenu__sub-collection-products--expanded');
      }
    });
  }

  /**
   * Close all open sub-collections
   */
  function closeAllSubCollections() {
    megaMenuToggles.forEach(toggle => {
      const targetId = toggle.getAttribute('aria-controls');
      if (!targetId) return;
      
      // Find within the toggle's parent panel context (fixes duplicate ID issue)
      const parentPanel = toggle.closest('.megamenu__content-panel');
      const targetElement = parentPanel ? parentPanel.querySelector(`#${targetId}`) : document.getElementById(targetId);
      
      if (targetElement) {
        toggle.setAttribute('aria-expanded', 'false');
        targetElement.setAttribute('aria-hidden', 'true');
        toggle.classList.remove('megamenu__collection-product-button--expanded');
        targetElement.classList.remove('megamenu__sub-collection-products--expanded');
      }
    });
  }

  /**
   * Handle keyboard navigation
   */
  function handleKeyboardNavigation() {
    document.addEventListener('keydown', function(event) {
      switch(event.key) {
        case 'Escape':
          // Preserve accordion states on escape - don't close them
          // Focus first toggle button instead
          const focusTarget = /** @type {HTMLElement} */ (megaMenuToggles[0]);
          if (focusTarget) {
            focusTarget.focus();
          }
          break;
          
        case 'ArrowDown':
          // Navigate to next item in sub-collection
          if (event.target && /** @type {HTMLElement} */ (event.target).classList.contains('megamenu__sub-collection-product-link')) {
            event.preventDefault();
            const nextLink = getNextFocusableElement(/** @type {HTMLElement} */ (event.target));
            if (nextLink) nextLink.focus();
          }
          break;
          
        case 'ArrowUp':
          // Navigate to previous item in sub-collection
          if (event.target && /** @type {HTMLElement} */ (event.target).classList.contains('megamenu__sub-collection-product-link')) {
            event.preventDefault();
            const prevLink = getPreviousFocusableElement(/** @type {HTMLElement} */ (event.target));
            if (prevLink) prevLink.focus();
          }
          break;
      }
    });
  }

  /**
   * Get next focusable element in sub-collection
   * @param {HTMLElement} currentElement - Current focused element
   * @returns {HTMLElement|null} Next focusable element
   */
  function getNextFocusableElement(currentElement) {
    const subCollection = currentElement.closest('.megamenu__sub-collection-products');
    if (!subCollection) return null;
    
    const links = Array.from(subCollection.querySelectorAll('.megamenu__sub-collection-product-link'));
    const currentIndex = links.indexOf(currentElement);
    return /** @type {HTMLElement} */ (links[currentIndex + 1] || links[0]); // Loop to first if at end
  }

  /**
   * Get previous focusable element in sub-collection
   * @param {HTMLElement} currentElement - Current focused element
   * @returns {HTMLElement|null} Previous focusable element
   */
  function getPreviousFocusableElement(currentElement) {
    const subCollection = currentElement.closest('.megamenu__sub-collection-products');
    if (!subCollection) return null;
    
    const links = Array.from(subCollection.querySelectorAll('.megamenu__sub-collection-product-link'));
    const currentIndex = links.indexOf(currentElement);
    return /** @type {HTMLElement} */ (links[currentIndex - 1] || links[links.length - 1]); // Loop to last if at beginning
  }

  /**
   * Auto-expand first accordion item in first panel
   * Used both on page load and when menu opens to ensure accordion stays expanded
   */
  function ensureFirstAccordionExpanded() {
    const firstNavButton = navToggleButtons[0];
    if (!firstNavButton) return;
    
    const targetPanelId = firstNavButton.getAttribute('aria-controls');
    if (!targetPanelId) return;
    
    const targetPanel = document.getElementById(targetPanelId);
    if (!targetPanel) return;
    
    // Auto-expand first accordion item in the first panel
    autoExpandFirstAccordion(targetPanel);
  }

  /**
   * Handle window resize to clean up states and reset mega menu
   */
  function handleWindowResize() {
    let previouslyMobile = isMobile();
    
    window.addEventListener('resize', function() {
      const currentlyMobile = isMobile();
      
      // Clean up mobile classes when resizing to desktop
      if (!currentlyMobile) {
        const column1 = document.querySelector('[data-megamenu-column-1]');
        const column2 = document.querySelector('[data-megamenu-column-2]');
        
        if (column1) {
          column1.classList.remove('megamenu__column-1--hidden');
        }
        if (column2) {
          column2.classList.remove('megamenu__column-2--full-width');
        }
      }
      
      // Reset mega menu state when transitioning from desktop to mobile
      if (!previouslyMobile && currentlyMobile) {
        resetMegaMenuState();
      }
      
      // Update previous state
      previouslyMobile = currentlyMobile;
    });
  }

  /**
   * Reset all mega menu states (close panels, nav buttons, sub-collections)
   */
  function resetMegaMenuState() {
    // Close all sub-collections
    closeAllSubCollections();
    
    // Reset all nav buttons to inactive state
    navToggleButtons.forEach(button => {
      button.setAttribute('aria-expanded', 'false');
      button.classList.remove('nav__link--active');
    });
    
    // Hide all content panels
    const allPanels = document.querySelectorAll('.megamenu__content-panel');
    allPanels.forEach(panel => {
      panel.setAttribute('aria-hidden', 'true');
      panel.classList.remove('megamenu__content-panel--active');
    });
    
    // Reset mobile column states
    toggleMobileColumns(false);
  }

  /**
   * Watch for drawer open/close events and activate first panel on desktop
   */
  function handleDrawerStateChanges() {
    // Observer to watch for changes to body class (menu-is-expanded)
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const bodyHasMenuExpanded = document.body.classList.contains('menu-is-expanded');
          
          // When drawer opens on desktop, activate first panel
          if (bodyHasMenuExpanded && isDesktop()) {
            // Use setTimeout to ensure DOM is ready
            setTimeout(() => {
              activateFirstPanelOnDesktop();
            }, 0);
          }
        }
      });
    });
    
    // Start observing body class changes
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  /**
   * Handle click outside megamenu (preserves accordion states)
   */
  function handleClickOutside() {
    document.addEventListener('click', function(event) {
      const target = /** @type {HTMLElement} */ (event.target);
      if (!target) return;
      
      // Multiple robust checks to detect if click is inside megamenu
      const megaMenuElements = document.querySelectorAll('.megamenu');
      const isInsideAnyMegaMenu = Array.from(megaMenuElements).some(menu => 
        menu.contains(target) || target === menu
      );
      
      const isInsideMegaMenu = target.closest('.megamenu') || target.classList.contains('megamenu');
      const isToggleButton = target.closest('[data-megamenu-toggle]') || target.hasAttribute('data-megamenu-toggle');
      const isNavToggle = target.closest('[data-megamenu-nav-toggle]') || target.hasAttribute('data-megamenu-nav-toggle');
      const isMegaMenuChild = target.closest('[data-megamenu]') !== null;
      
      // Only close if ALL checks confirm it's outside the megamenu
      const shouldClose = !isInsideMegaMenu && 
                         !isToggleButton && 
                         !isNavToggle &&
                         !isInsideAnyMegaMenu && 
                         !isMegaMenuChild;
      
      if (shouldClose) {
        // Don't close accordions when clicking outside - preserve their state
        // closeAllSubCollections();
      } else {
        // Prevent any other handlers from accidentally closing
        event.stopImmediatePropagation();
      }
    });
  }

  // Initialize all functionality
  initMegaMenuToggles();
  initNavToggles();
  initCollectionProductsButtons();
  handleKeyboardNavigation();
  handleClickOutside();
  handleWindowResize();
  handleDrawerStateChanges();
  
  // Auto-expand first accordion item on page load
  ensureFirstAccordionExpanded();
});
